import axios from "axios";

/**
 * SuperadminService - API service for superadmin functionality
 * Handles all superadmin-related API calls including user management, clinic management, and system statistics
 */
class SuperadminService {
  /**
   * Check superadmin health and authentication
   * @returns {Promise} API response
   */
  static async checkHealth() {
    return axios.get('/api/superadmin/health');
  }

  /**
   * Get system statistics
   * @returns {Promise} API response with system stats
   */
  static async getSystemStats() {
    return axios.get('/api/superadmin/stats');
  }

  // ADMIN MCP (cross-clinic AI connector) METHODS

  /**
   * Get the admin MCP overview: connector URL, connections and recent tool calls
   * @returns {Promise} API response
   */
  static async getMcpAdmin() {
    return axios.get('/api/superadmin/mcpAdmin');
  }

  /**
   * Revoke one admin MCP connection
   * @param {number} connectionId - Admin MCP connection ID
   * @returns {Promise} API response with the revoked connection
   */
  static async revokeMcpAdminConnection(connectionId) {
    return axios.post(`/api/superadmin/mcpAdmin/connections/${connectionId}/revoke`);
  }

  // BILLING METHODS

  /**
   * แพ็กทั้งหมดของสาขาหนึ่ง ไว้ให้ทีมเลือกก่อนคืนเงิน
   *
   * เข้ามาจากปุ่มในหน้าค้นหาสาขา เพราะเลข subscription ไม่โผล่ที่ไหนให้ทีมเห็นเลย
   * รวมแถวที่หมดอายุแล้วด้วย — คำขอคืนเงินมักมาหลังแพ็กหมดอายุไปแล้ว
   * @param {number} branchId
   */
  static async listBranchSubscriptions(branchId) {
    return axios.get(`/api/superadmin/billing/branches/${branchId}/subscriptions`);
  }

  /**
   * ยอดคืนที่ระบบเสนอให้เลือก — อ่านอย่างเดียว ไม่เปลี่ยนอะไร เปิดดูกี่ครั้งก็ได้
   * @param {number} subscriptionId
   */
  static async getRefundQuote(subscriptionId) {
    return axios.get(`/api/superadmin/billing/subscriptions/${subscriptionId}/refund-quote`);
  }

  /**
   * คืนเงินและเพิกถอนสิทธิ์ทันที
   *
   * amountBaht = 0 ใช้ได้และเป็นกรณีปกติ (เพิกถอนโดยไม่คืนเงิน)
   * providerRefundRef บังคับเมื่อคืนเงินจริง ระหว่างที่ยังต่อการคืนเงินอัตโนมัติไม่ได้
   * @param {number} subscriptionId
   * @param {{ amountBaht: number, reason: string, providerRefundRef?: string }} payload
   */
  static async refundSubscription(subscriptionId, payload) {
    return axios.post(
      `/api/superadmin/billing/subscriptions/${subscriptionId}/refund`,
      payload
    );
  }

  // USER MANAGEMENT METHODS

  /**
   * Search users with advanced filtering
   * @param {Object} params - Search parameters
   * @param {string} params.firstName - Search by first name
   * @param {string} params.lastName - Search by last name  
   * @param {string} params.email - Search by email
   * @param {string} params.mobile - Search by phone number
   * @param {string} params.licenseNumber - Search by doctor license
   * @param {number} params.userTypeId - Filter by user type (1=doctor, 2=staff)
   * @param {number} params.statusId - Filter by status (0=inactive, 1=active)
   * @param {string} params.clinicCode - Filter users from specific clinic
   * @param {string} params.branchCode - Filter users from specific branch
   * @param {boolean} params.hasLicense - Filter doctors with/without license
   * @param {string} params.sortBy - Sort field: creation_dt, email, first_name_th, dr_license_no
   * @param {string} params.sortOrder - Sort direction: ASC, DESC
   * @param {number} params.limit - Results per page (default: 20)
   * @param {number} params.offset - Pagination offset (default: 0)
   * @returns {Promise} API response with user search results
   */
  static async searchUsers(params = {}) {
    return axios.get('/api/superadmin/users/search', { params });
  }

  /**
   * Get detailed information about a specific user
   * @param {number} userId - User ID
   * @returns {Promise} API response with user details
   */
  static async getUserDetails(userId) {
    return axios.get(`/api/superadmin/users/${userId}`);
  }

  /**
   * Update user status (activate/deactivate)
   * @param {number} userId - User ID
   * @param {Object} statusData - Status update data
   * @param {number} statusData.statusId - New status (0=inactive, 1=active)
   * @param {string} statusData.reason - Optional reason for status change
   * @returns {Promise} API response
   */
  static async updateUserStatus(userId, statusData) {
    return axios.put(`/api/superadmin/users/${userId}/status`, statusData);
  }

  // CLINIC MANAGEMENT METHODS

  /**
   * Search clinics with advanced filtering
   * @param {Object} params - Search parameters
   * @param {string} params.clinicName - Search by clinic name (Thai/English)
   * @param {string} params.address - Search by clinic or branch address
   * @param {string} params.telephone - Search by clinic or branch phone
   * @param {string} params.branchName - Search by branch name (Thai/English)
   * @param {string} params.taxId - Search by tax ID
   * @param {boolean} params.hasActiveBranches - Filter clinics with active branches
   * @param {number} params.minMembers - Minimum number of members
   * @param {number} params.maxMembers - Maximum number of members
   * @param {string} params.sortBy - Sort field: creation_dt, clinic_name_th, total_members, total_branches
   * @param {string} params.sortOrder - Sort direction: ASC, DESC
   * @param {number} params.limit - Results per page (default: 20)
   * @param {number} params.offset - Pagination offset (default: 0)
   * @returns {Promise} API response with clinic search results
   */
  static async searchClinics(params = {}) {
    return axios.get('/api/superadmin/clinics/search', { params });
  }

  /**
   * Get detailed information about a specific clinic
   * @param {string} clinicCode - Clinic code
   * @returns {Promise} API response with clinic details
   */
  static async getClinicDetails(clinicCode) {
    return axios.get(`/api/superadmin/clinics/${clinicCode}`);
  }

  // ADS MANAGEMENT METHODS

  /**
   * Increment ads version to force update advertisements
   * @returns {Promise} API response with version update details
   */
  static async incrementAdsVersion() {
    return axios.post('/api/superadmin/config/ads-version/increment');
  }

  // UTILITY METHODS

  /**
   * Build query parameters object, removing empty/null values
   * @param {Object} params - Raw parameters
   * @returns {Object} Cleaned parameters object
   */
  static cleanParams(params) {
    const cleaned = {};
    Object.keys(params).forEach(key => {
      const value = params[key];
      if (value !== null && value !== undefined && value !== '') {
        cleaned[key] = value;
      }
    });
    return cleaned;
  }

  /**
   * Build search URL with pagination
   * @param {string} baseUrl - Base API URL
   * @param {Object} params - Search parameters
   * @param {number} page - Current page (1-based)
   * @param {number} limit - Items per page
   * @returns {Object} URL parameters with pagination
   */
  static buildPaginatedParams(params, page = 1, limit = 20) {
    return {
      ...this.cleanParams(params),
      limit,
      offset: (page - 1) * limit
    };
  }

  // CLINIC/BRANCH CREATION METHODS

  /**
   * Create a new clinic with initial branch via Excel upload
   * @param {FormData} formData - Contains file and nhso flag
   * @returns {Promise} API response
   */
  static async createClinic(formData) {
    return axios.post('/api/superadmin/clinics', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  /**
   * Create a new branch for existing clinic via Excel upload
   * @param {FormData} formData - Contains file, clinicId, uid, nhso
   * @returns {Promise} API response
   */
  static async createBranch(formData) {
    return axios.post('/api/superadmin/branches', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  /**
   * Bulk import patients from Excel file
   * @param {FormData} formData - Contains file, clinicId, branchId
   * @returns {Promise} API response with import results
   */
  static async importPatients(formData) {
    return axios.post('/api/superadmin/patients/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  /**
   * Import a treatment master from a legacy ProcedureCode export.
   * Section names belong to the exporting clinic, so formData carries a
   * categoryMap; send dryRun=true first to get the sections to map.
   * @param {FormData} formData - file, clinicId, branchId, categoryMap, codeMap, dryRun
   * @returns {Promise} API response
   */
  static async importTreatments(formData) {
    return axios.post('/api/superadmin/treatments/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  // PROTOCOL MEDIA GALLERY

  /**
   * List protocol media across every clinic. Rows carry a presigned
   * originalURL, and a presigned thumbnailURL where one already exists.
   * @param {Object} params - limit, offset, clinicId, from, to
   * @returns {Promise} API response with data and pagination
   */
  static async searchProtocolMedia(params = {}) {
    return axios.get('/api/superadmin/protocolMedia', { params });
  }

  /**
   * Resolve presigned thumbnail URLs for a batch of media files, generating
   * any that do not exist yet.
   *
   * Thumbnails cannot be requested straight from an <img> src: the API
   * authenticates with an Authorization header, which a browser will not
   * attach to an image request. Going through axios gets the presigned S3 URL,
   * and that URL carries its own credentials, so the img tag can use it.
   *
   * @param {number[]} ids - at most 24 per call
   * @returns {Promise} API response with a { urls: { id: url|null } } map
   */
  static async generateProtocolMediaThumbnails(ids) {
    return axios.post('/api/superadmin/protocolMedia/thumbnails', { ids });
  }
}

export default SuperadminService;